import { useCallback, useState } from 'react';

// Shared hover/focus state for an interactive chart. show(x, y, content) records
// the anchor point (in chart-pixel coords) + the tooltip node; hide() clears it.
// Drives both mouse hover and keyboard focus, so charts are operable either way.
export function useChartHover() {
  const [hover, setHover] = useState(null); // { x, y, content } | null
  const show = useCallback((x, y, content) => setHover({ x, y, content }), []);
  const hide = useCallback(() => setHover(null), []);
  return { hover, show, hide };
}

// Theme-safe HTML overlay tooltip, positioned at the hovered point. Rendered
// inside a position:relative chart wrapper; flips to the left of the anchor past
// the chart midpoint so it never clips the right edge. Colors come from theme
// CSS vars (never a Tailwind opacity class).
export function ChartTooltip({ hover, width }) {
  if (!hover) return null;
  const onRight = width > 0 && hover.x > width / 2;
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute z-10 rounded-md border px-2 py-1 text-[11px] shadow-lg"
      style={{
        left: hover.x,
        top: hover.y,
        transform: `translate(${onRight ? '-100%' : '0'}, calc(-100% - 6px))`,
        background: 'var(--c-surface-2)',
        borderColor: 'var(--c-border)',
        color: 'var(--c-text-2)',
        whiteSpace: 'nowrap',
        maxWidth: '16rem',
      }}
    >
      {hover.content}
    </div>
  );
}
